import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CheckboxFormInput,
    SelectFormInput,
    TextFormInput,
} from "src/components/form";
import {
    GroupTrialLaboratoryFinanceBreadcrumbs,
    TrialGroupLaboratoryFinanceBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import {
    GroupTrialLabSubMenu,
    TrialGroupLabSubMenu,
} from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITrialGroupLaboratoryFinanceReduxFormState } from "src/reducers/reactReduxForms/trialGroupLaboratoryFinance";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupLaboratoryFinanceActions from "../../../actions/trialGroupLaboratoryFinance";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialGroupLaboratoryFinancePage.scss";

interface TrialGroupLaboratoryFinancePageProps {
    form: Dtos.TrialGroupLaboratoryFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialGroupLaboratoryFinanceReduxFormState;
    trialGroupLaboratoryFinance: Dtos.TrialGroupLaboratoryFinance;
    lookups: Dtos.Lookup[];
    loadingTrialGroupLaboratoryFinance: boolean;
    loadTrialGroupLaboratoryFinanceSuccess: boolean;
    loadTrialGroupLaboratoryFinanceFailure: boolean;
    loadTrialGroupLaboratoryFinanceErrorCode: string;
    savingTrialGroupLaboratoryFinance: boolean;
    saveTrialGroupLaboratoryFinanceSuccess: boolean;
    saveTrialGroupLaboratoryFinanceFailure: boolean;
    validatingTrialGroupLaboratoryFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroupLaboratoryFinance: TrialGroupLaboratoryFinanceActions.ITrialGroupLaboratoryFinanceClearActionCreator;
    loadTrialGroupLaboratoryFinance: TrialGroupLaboratoryFinanceActions.ITrialGroupLaboratoryFinanceLoadByTrialGroupLaboratoryIdActionCreator;
    saveTrialGroupLaboratoryFinance: TrialGroupLaboratoryFinanceActions.ITrialGroupLaboratoryFinanceSaveActionCreator;
    validateTrialGroupLaboratoryFinance: TrialGroupLaboratoryFinanceActions.ITrialGroupLaboratoryFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupLaboratoryFinance";

const TrialGroupLaboratoryFinancePage: FC<
    TrialGroupLaboratoryFinancePageProps
> = ({
    form,
    formState,
    formProperties,
    trialGroupLaboratoryFinance,
    lookups,
    loadingTrialGroupLaboratoryFinance,
    loadTrialGroupLaboratoryFinanceFailure,
    loadTrialGroupLaboratoryFinanceErrorCode,
    savingTrialGroupLaboratoryFinance,
    saveTrialGroupLaboratoryFinanceSuccess,
    saveTrialGroupLaboratoryFinanceFailure,
    validatingTrialGroupLaboratoryFinance,
    validationFailures,
    permissions,
    clearTrialGroupLaboratoryFinance,
    loadTrialGroupLaboratoryFinance,
    saveTrialGroupLaboratoryFinance,
    validateTrialGroupLaboratoryFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupLaboratoryId } = useParams<{
        trialGroupLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupLaboratoryIdNumber = parseInt(trialGroupLaboratoryId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();


    // Load Trial Group Laboratory Finance
    useEffect(() => {
        if (
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratoryFinance &&
            !loadingTrialGroupLaboratoryFinance
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryFinanceBreadcrumbs
                    : GroupTrialLaboratoryFinanceBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber
                )
            );

            loadTrialGroupLaboratoryFinance(trialGroupLaboratoryIdNumber);
        } else if (trialGroupLaboratoryFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryFinance,
        permissions,
        formInitalised,
        setBreadcrumbs,
        loadTrialGroupLaboratoryFinance,
        TrialGroupLaboratoryFinanceBreadcrumbs,
        GroupTrialLaboratoryFinanceBreadcrumbs,
        setFormInitalised,
        loadingTrialGroupLaboratoryFinance,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupLaboratoryFinanceFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryFinanceErrorCode}`);
        }
    }, [
        loadTrialGroupLaboratoryFinanceFailure,
        loadTrialGroupLaboratoryFinanceErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratoryFinance?.trialGroupLaboratory
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryFinanceBreadcrumbs
                    : GroupTrialLaboratoryFinanceBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupLabSubMenu
                    : GroupTrialLabSubMenu;

            setDrawerItems(
                drawerItemSetter(
                    trialGroupLaboratoryFinance?.trialGroupLaboratory,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryFinance.trialGroupLaboratory?.trialGroup
                        ?.trial?.displayName,
                    trialGroupLaboratoryFinance.trialGroupLaboratory?.trialGroup
                        ?.group?.name,
                    trialGroupLaboratoryFinance.trialGroupLaboratory?.laboratory
                        ?.name
                )
            );
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryFinance,
        setBreadcrumbs,
        setDrawerItems,
        TrialGroupLaboratoryFinanceBreadcrumbs,
        GroupTrialLaboratoryFinanceBreadcrumbs,
        TrialGroupLabSubMenu,
        GroupTrialLabSubMenu,
    ]);

    // Reload trial group laboratory finance after saving
    useEffect(() => {
        if (
            !savingTrialGroupLaboratoryFinance &&
            saveTrialGroupLaboratoryFinanceSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group laboratory finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];

            if (saveAndReturn) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs`;
                navigate(url);
            } else if (!trialGroupLaboratoryIdNumber) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryFinance.trialGroupLaboratory?.id}/finance`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryFinance.trialGroupLaboratory?.id}/finance`;
                navigate(url);
                clearTrialGroupLaboratoryFinance();
            }
        }
    }, [
        savingTrialGroupLaboratoryFinance,
        saveTrialGroupLaboratoryFinanceSuccess,
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        navigate,
        saveAndReturn,
        clearTrialGroupLaboratoryFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialGroupLaboratoryFinance &&
            saveTrialGroupLaboratoryFinanceFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialGroupLaboratoryFinance,
        saveTrialGroupLaboratoryFinanceSuccess,
    ]);

    // Cleanup effect to clear the trial group laboratory finance on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupLaboratoryFinance();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroupLaboratoryFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupLaboratoryFinance =
                {} as Dtos.TrialGroupLaboratoryFinance;

            if (!data.trialGroupLaboratoryId) {
                changes.trialGroupLaboratoryId = trialGroupLaboratoryIdNumber;
            }

            await saveTrialGroupLaboratoryFinance(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupLaboratoryFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs`;
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupLaboratoryFinance =
            {} as Dtos.TrialGroupLaboratoryFinance;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupLaboratoryFinance }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupLaboratoryFinance =
                {} as Dtos.TrialGroupLaboratoryFinance;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialGroupLaboratoryFinance
            ) {
                await validateTrialGroupLaboratoryFinance(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialGroupLaboratoryIdNumber,
                null,
                permissions,
                Dtos.Permission.TrialGroupLaboratoryFinanceUpdate,
                Dtos.Permission.TrialGroupLaboratoryFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupLaboratoryFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            overrideFundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Group Email Address",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            fundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overrideFundingEmail,
                                                inputProps: {
                                                    value: !form.overrideFundingEmail
                                                        ? trialGroupLaboratoryFinance
                                                                ?.trialGroupLaboratory
                                                                ?.laboratory
                                                                ?.laboratoryFinance
                                                                ?.fundingEmailAddress
                                                        : form.fundingEmail,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            accountNumber: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            bsbSwift: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            siteCostCentre: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            overridePaymentDetails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Payment Details",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            abn: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialGroupLaboratoryFinance
                                                                ?.trialGroupLaboratory
                                                                ?.laboratory?.abn
                                                        : form.abn,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialGroupLaboratoryFinance
                                                                ?.trialGroupLaboratory
                                                                ?.laboratory
                                                                ?.laboratoryFinance
                                                                ?.taxInvoiceName
                                                        : form.taxInvoiceName,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialGroupLaboratoryFinance
                                                                ?.trialGroupLaboratory
                                                                ?.laboratory
                                                                ?.laboratoryFinance
                                                                ?.quickbooksTaxCodeId
                                                        : form.quickbooksTaxCodeId,
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "GroupFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialGroupLaboratoryFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialGroupLaboratoryFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialGroupLaboratoryFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={
                loadingTrialGroupLaboratoryFinance ||
                savingTrialGroupLaboratoryFinance
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoryFinancePageProps> => {
    let trialGroupLaboratoryFinance:
        | Dtos.TrialGroupLaboratoryFinance
        | undefined = !(
        state.trialGroupLaboratoryFinance.formData instanceof Array
    )
        ? state.trialGroupLaboratoryFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupLaboratoryFinance: trialGroupLaboratoryFinance!,
        loadingTrialGroupLaboratoryFinance:
            state.trialGroupLaboratoryFinance.loadState &&
            state.trialGroupLaboratoryFinance.loadState.status ==
                RequestState.Pending,
        loadTrialGroupLaboratoryFinanceSuccess:
            state.trialGroupLaboratoryFinance.loadState &&
            state.trialGroupLaboratoryFinance.loadState.status ==
                RequestState.Success,
        loadTrialGroupLaboratoryFinanceFailure:
            state.trialGroupLaboratoryFinance.loadState &&
            state.trialGroupLaboratoryFinance.loadState.status ==
                RequestState.Failure,
        loadTrialGroupLaboratoryFinanceErrorCode:
            state.trialGroupLaboratoryFinance.loadState.errorCode,
        savingTrialGroupLaboratoryFinance:
            state.trialGroupLaboratoryFinance.saveState &&
            state.trialGroupLaboratoryFinance.saveState.status ==
                RequestState.Pending,
        saveTrialGroupLaboratoryFinanceSuccess:
            state.trialGroupLaboratoryFinance.saveState &&
            state.trialGroupLaboratoryFinance.saveState.status ==
                RequestState.Success,
        saveTrialGroupLaboratoryFinanceFailure:
            state.trialGroupLaboratoryFinance.saveState &&
            state.trialGroupLaboratoryFinance.saveState.status ==
                RequestState.Failure,
        validatingTrialGroupLaboratoryFinance:
            state.trialGroupLaboratoryFinance.validateState &&
            state.trialGroupLaboratoryFinance.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialGroupLaboratoryFinance.lookups,
        form: state.reduxForms.trialGroupLaboratoryFinance,
        formState: state.trialGroupLaboratoryFinance.formState,
        formProperties: state.trialGroupLaboratoryFinance.formProperties,
        reduxFormState: state.reduxForms.formState.trialGroupLaboratoryFinance,
        validationFailures: trialGroupLaboratoryFinance
            ? state.trialGroupLaboratoryFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoryFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupLaboratoryFinance: bindActionCreators(
            TrialGroupLaboratoryFinanceActions.LoadTrialGroupLaboratoryFinanceByTrialGroupLaboratoryId,
            dispatch
        ),
        clearTrialGroupLaboratoryFinance: bindActionCreators(
            TrialGroupLaboratoryFinanceActions.Clear,
            dispatch
        ),
        saveTrialGroupLaboratoryFinance: bindActionCreators(
            TrialGroupLaboratoryFinanceActions.SaveTrialGroupLaboratoryFinance,
            dispatch
        ),
        validateTrialGroupLaboratoryFinance: bindActionCreators(
            TrialGroupLaboratoryFinanceActions.ValidateTrialGroupLaboratoryFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupLaboratoryFinancePage);
